import {Component, Inject, OnInit} from "@angular/core";
import {ProjectId} from "../../types/ids";
import {ActivatedRoute} from "@angular/router";
import {browserFromCapability, Capability} from "../../types/capability";
import {Browser, browserFromBackendObject} from '../../types/browser';
import {VehicleService} from "@webmate-web-core/shared/services/vehicle.service";
import {VehicleOffer} from "@webmate-web-core/shared/types/vehicles";
import {copyToClipboard} from '@webmate-web-core/shared/utils/clipboard';
import {isDefined} from '@webmate-web-core/shared/utils/utils';
import {WebmateToolConfig} from '../modaltoolcontainer/modaltoolcontainer.service';
import {TOOL_CONTAINER_DATA} from '../modaltoolcontainer/modaltoolcontainer.component';
import {Device} from '@webmate-web-core/shared/types/devices/device';
import {DevicePropertyNames} from '@webmate-web-core/shared/types/devices/device-properties';

export class SeleniumCapabilityGeneratorToolConfig implements WebmateToolConfig {
    toolName = "Selenium Capabilities Generator";
    scrollable = true;

    constructor(public projectId: ProjectId, public device?: Device) {}
}

@Component({
    templateUrl: './selenium-capability-generator.component.html',
    styleUrls: ['./selenium-capability-generator.component.less']
})
export class SeleniumCapabilityGeneratorComponent implements OnInit {

    busy = true;
    triggeredCopyToClipboard = false;

    private projectId: ProjectId;
    public device?: Device;

    capabilities: Capability[];
    browsers: Browser[];
    chosenCapability: Capability;
    selectedFormat: 'java' | 'json' = 'java';
    isMobileDevice: boolean = false;

    constructor(@Inject(TOOL_CONTAINER_DATA) private data: SeleniumCapabilityGeneratorToolConfig,
                private vehicleService: VehicleService) {
        this.projectId = data.projectId;
        this.device = data.device;
    }

    makeCapabilityFromOffer(offer: VehicleOffer): Capability {
        return offer['vehicle.browser'] as any as Capability;
    }

    makeCapabilityFromBrowser(browser: Browser): Capability {
        return {
            browserType: browser.browserType,
            version: browser.version,
            platform: browser.platform.toBackendString(),
            supportsProxy: false, // Set to false. Browser does not have this information, but the capabilities generator does not need it
            model: browser.model
        } as Capability;
    }

    ngOnInit() {
        if (!!this.device) {
            const machineBrowsers = this.device.properties[DevicePropertyNames.MachineBrowsers] || [];
            this.browsers = machineBrowsers.map(b => browserFromBackendObject(b)!);
            this.capabilities = this.browsers.map(this.makeCapabilityFromBrowser);
            this.busy = false;
        } else {
            this.vehicleService.getVehicleOffersForProject$(this.projectId).subscribe(offers => {
                this.capabilities = offers.map(o => {
                    let cap = this.makeCapabilityFromOffer(o!.properties);
                    cap.browserType = cap.browserType.toLowerCase();
                    return cap;
                }).sort((a, b) => a.browserType.localeCompare(b.browserType))
                ;
                this.browsers = this.capabilities.map(x => browserFromCapability(x!));
                this.busy = false;
            });
        }
    }

    setActiveBrowser(event: Browser) {
        let index = this.browsers.indexOf(event);
        this.chosenCapability = this.capabilities[index];
        this.triggeredCopyToClipboard = false;
        this.isMobileDevice = this.chosenCapability.platform.toLowerCase().includes('ios') || this.chosenCapability.platform.toLowerCase().includes('android');
    }

    copyToClipboard(capability: Capability): void {
        let textToCopy;
        if (this.selectedFormat === 'java') {
            textToCopy = this.getJavaCodeFromCapability(capability, false);
        } else {
            textToCopy = this.getJSONCodeFromCapability(capability, false);
        }
        copyToClipboard(textToCopy).then(() =>
            this.triggeredCopyToClipboard = true
        );
    }

    getJavaCodeFromCapability(capability: Capability, htmlEmbedded: boolean): string {
        const newLine = htmlEmbedded ? '<br>' : '\n';
        let capabilitiesCode = 'DesiredCapabilities caps = new DesiredCapabilities();';
        capabilitiesCode += `${newLine}caps.setCapability("browserName", "${capability.browserType}");`;
        if (capability.browserType.toLowerCase() !== 'appium') {
            capabilitiesCode += `${newLine}caps.setCapability("browserVersion", "${capability.version}");`;
        }
        capabilitiesCode += `${newLine}caps.setCapability("platformName", "${capability.platform}");`;
        if (capability.model) {
            capabilitiesCode += `${newLine}caps.setCapability("wm:model", "${capability.model}");`;
        }
        return capabilitiesCode;
    }

    getJSONCodeFromCapability(capability: Capability, htmlEmbedded: boolean): string {
        const newLine = htmlEmbedded ? '<br>' : '\n';
        const caps: any = {
            browserName: capability.browserType,
            platformName: capability.platform
        };
        if (capability.browserType.toLowerCase() !== 'appium') {
            caps.browserVersion = capability.version;
        }
        if (capability.model) {
            caps["wm:model"] = capability.model;
        }
        return JSON.stringify(caps, null, "\t").replace(/\n/g, newLine);
    }

}
